// =============================================================================
// io/sync/savePattern.ts — create, update and delete a pattern.
// -----------------------------------------------------------------------------
// ── 🚨 THE SAVE RUNS THE LIFECYCLE. IT IS NOT A FIELD COPY ──
//
// `reconcileVersions` runs on every save, and it is the whole reason this file
// exists rather than a generic row writer. Editing a PRODUCTION pattern's rules
// must land in a CANDIDATE version and leave the live one frozen, because trades
// are pinned to the version that was live when they were tagged.
//
// A writer that just wrote the form's fields would look correct, save cleanly,
// and silently re-judge every past trade against rules written afterwards.
//
// ── 🚨 `extras` IS MERGED, NEVER REBUILT ──
//
// The lifecycle keys live in `extras` alongside things this client does not model
// — and the journal side already learned this the expensive way: passing `{}` as
// the base blob took a contract from ten keys to five and nulled two breach
// timestamps. So the stored blob is spread first and the lifecycle written over
// it, never the other way round.
//
// ⚠ `patternToRow` takes the FULL prior pattern for exactly that reason. There is
// no overload that omits it, because the omitting call is the one that loses data
// and it type-checks perfectly.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import {
  reconcileVersions, type PatternLifecycle, type VersionedPattern,
} from '../../domain/patternVersions';
import type { PatternSummary } from './patterns';
import { reportFailureAs } from '../diagnostics/errorLog';

/**
 * Everything the Edit Pattern form owns.
 *
 * ── 🚨 EVERY FIELD HERE IS RENDERED BY THE FORM, AND THAT IS THE CONTRACT ──
 *
 * D61: do not RENDER a field you have not read; D62: do not SEND a column you do
 * not own. Both hold here for a reason that is worth stating rather than assuming,
 * because it is different from the trade form's:
 *
 *   `loadPatterns` selects every column this patch writes, and the form puts every
 *   one of them on screen. There is no partial read to guard against.
 *
 * ⚠ The columns NOT in this list — `entry_trigger`'s neighbours `stop_rule`,
 * `target_rule`, `scale_out_rules`, plus `screenshots` — are absent from
 * `patternToRow` too, so an UPDATE never names them and they survive untouched.
 * Adding a field here without also rendering it is the write D46 forbids.
 */
export interface PatternPatch {
  name: string;
  description: string;
  /** The LEGACY free-text block. Editable but collapsed by default. */
  criteria: string;
  /** ⚠ The second half of the legacy block — see `PatternSummary.entryTrigger`. */
  entryTrigger: string;
  invalidation: string;
  entryCriteria: { id: string; label: string }[];
  category: string;
  sessions: string[];
  regimes: string[];
  daysOfWeek: number[];
  priorDayRel: string;
  news: string;
}

export interface SavePatternResult {
  id: string | null;
  error: string | null;
}

/**
 * ⚠ A NAME IS REQUIRED. An unnamed pattern renders as "(untitled)" in every
 * picker and leaderboard, and the trade form makes it a required field the moment
 * one is tagged.
 */
export function validatePattern(p: PatternPatch): string | null {
  if (!p.name.trim()) return 'Give the pattern a name — it has to be pickable on the trade form.';
  return null;
}

/**
 * Build the row, running the lifecycle over the edited rules.
 *
 * @param prior the pattern as loaded, for its stored `extras` and version list.
 *              `null` only for a brand-new pattern.
 * @param now   an ISO timestamp — `domain/` has no clock.
 */
export function patternToRow(
  patch: PatternPatch,
  prior: PatternSummary | null,
  userId: string,
  now: string,
): Record<string, unknown> {
  const priorExtras = prior?.extras ?? {};

  // ⚠ A NEW pattern starts as a CANDIDATE, matching the old client: it is not
  // taggable on live trades until promoted, so an untested idea cannot quietly
  // start collecting real money against it.
  const status = typeof priorExtras['status'] === 'string' && priorExtras['status']
    ? (priorExtras['status'] as string)
    : 'candidate';

  const working: VersionedPattern = {
    status,
    entryCriteria: patch.entryCriteria,
    invalidation: patch.invalidation,
    versions: (Array.isArray(priorExtras['versions']) ? priorExtras['versions'] : []) as never,
    liveVersion: Number(priorExtras['liveVersion']) || 1,
    candidateVersion: prior?.candidateVersion ?? null,
    ...(priorExtras['createdAt'] !== undefined
      ? { createdAt: priorExtras['createdAt'] as string }
      : { createdAt: now }),
  };

  const lifecycle: PatternLifecycle = reconcileVersions(working, now);

  return {
    user_id: userId,
    name: patch.name,
    category: patch.category || null,
    description: patch.description || null,
    criteria: patch.criteria || null,
    entry_trigger: patch.entryTrigger || null,
    invalidation: patch.invalidation || null,
    sessions: patch.sessions,
    regimes: patch.regimes,
    days_of_week: patch.daysOfWeek,
    prior_day_rel: patch.priorDayRel || null,
    news: patch.news || null,
    // ⚠ SPREAD FIRST, lifecycle over the top. Reversing these two lines loses
    // every key this client does not model.
    extras: {
      ...priorExtras,
      status: lifecycle.status,
      versions: lifecycle.versions,
      liveVersion: lifecycle.liveVersion,
      candidateVersion: lifecycle.candidateVersion,
      entryCriteria: patch.entryCriteria,
      createdAt: lifecycle.createdAt ?? now,
    },
  };
}

export async function savePattern(
  sb: SupabaseClient, id: string, patch: PatternPatch,
  prior: PatternSummary, userId: string, now: string,
): Promise<SavePatternResult> {
  const invalid = validatePattern(patch);
  if (invalid) return { id: null, error: invalid };

  const { data, error } = await sb
    .from('patterns').update(patternToRow(patch, prior, userId, now)).eq('id', id).select('id');

  if (error) {
    return {
      id: null,
      error: reportFailureAs('save pattern', error, `Save failed: ${error.message}`),
    };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return {
      id: null,
      error: 'The save matched no rows, so nothing changed. The pattern may have been '
        + 'deleted, or it belongs to another user.',
    };
  }
  return { id, error: null };
}

export async function insertPattern(
  sb: SupabaseClient, id: string, patch: PatternPatch, userId: string, now: string,
): Promise<SavePatternResult> {
  const invalid = validatePattern(patch);
  if (invalid) return { id: null, error: invalid };
  if (!id) return { id: null, error: 'Refusing to insert a pattern with no id.' };

  const { error } = await sb
    .from('patterns').insert({ id, ...patternToRow(patch, null, userId, now) });
  if (error) {
    return {
      id: null,
      error: reportFailureAs('create pattern', error, `Save failed: ${error.message}`),
    };
  }
  return { id, error: null };
}

/**
 * Write a lifecycle change on its own — the two promote buttons and reject.
 *
 * ⚠ Separate from `savePattern` deliberately. Promoting is not an edit of the
 * rules, and routing it through the form's save would run `reconcileVersions`
 * over working rules the user never opened — which, on a pattern with an
 * in-flight candidate, would immediately re-create the thing just promoted.
 *
 * ── 🚨 `rules` IS NOT OPTIONAL DECORATION. REJECT CANNOT WORK WITHOUT IT ──
 *
 * `rejectVersion` returns reverted WORKING RULES as well as a lifecycle, and D76
 * says why: dropping the candidate without restoring the live version's rules
 * leaves the editor showing rules that exist in no version, and the next save
 * re-creates the candidate just rejected. Those working rules are NOT inside the
 * lifecycle — `entryCriteria` rides `extras` and `invalidation` is a column — so a
 * writer that took only the lifecycle could not express a reject at all.
 *
 * Pass `null` for the two promotes, which change no rules.
 */
export async function savePatternLifecycle(
  sb: SupabaseClient, id: string, prior: PatternSummary,
  lifecycle: PatternLifecycle, userId: string,
  rules: { entryCriteria: { id: string; label: string }[]; invalidation: string } | null,
): Promise<SavePatternResult> {
  const { data, error } = await sb
    .from('patterns')
    .update({
      user_id: userId,
      // ⚠ Only written when the rules actually reverted. Naming the column
      // unconditionally would send `undefined` on a promote, which PostgREST
      // treats as an explicit null and would blank the invalidation text.
      ...(rules ? { invalidation: rules.invalidation || null } : {}),
      extras: {
        ...prior.extras,
        status: lifecycle.status,
        versions: lifecycle.versions,
        liveVersion: lifecycle.liveVersion,
        candidateVersion: lifecycle.candidateVersion,
        ...(rules ? { entryCriteria: rules.entryCriteria } : {}),
      },
    })
    .eq('id', id).select('id');

  if (error) {
    return {
      id: null,
      error: reportFailureAs('change pattern status', error, `Save failed: ${error.message}`),
    };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return { id: null, error: 'That change matched no rows — the pattern may have been deleted.' };
  }
  return { id, error: null };
}

/**
 * Delete a pattern.
 *
 * ⚠ `trades.pattern_id` is `ON DELETE SET NULL`, the same as exit models — this
 * does not fail when trades reference it, it UNTAGS them. And unlike an exit
 * model, a pattern carries its version history, so deleting also discards every
 * rule set the user ever tested. The caller has to say both.
 */
export async function deletePattern(
  sb: SupabaseClient, userId: string, id: string,
): Promise<{ error: string | null }> {
  const { data, error } = await sb
    .from('patterns').delete().eq('user_id', userId).eq('id', id).select('id');
  if (error) {
    return { error: reportFailureAs('delete pattern', error, `Delete failed: ${error.message}`) };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return { error: 'The delete matched no rows — the pattern may already be gone.' };
  }
  return { error: null };
}
